//* Vue
import { ref, computed, reactive, type SetupContext } from "vue";
//* 动画库
import { gsap } from "gsap";
//* 公共函数
import { utility } from "@various/utils";
//* 组件属性
import type { UiModalSideEmits, UiModalSideProps } from "../index";

export const useComposable = (define: UiModalSideProps, emit: SetupContext<typeof UiModalSideEmits>["emit"]) => {
    //* 响应式变量
    const refs = {
        open: ref<boolean>(false),
    };

    //* 节点
    const nodes = {
        main: ref<HTMLElement>(),
        container: ref<HTMLElement>(),
    };

    //* 计算属性
    const computeds = {
        site: computed(() => {
            switch (define.direction) {
                case "top":
                    return { leave: { yPercent: -100 }, enter: { yPercent: 0 } };

                case "left":
                    return { leave: { xPercent: -100 }, enter: { xPercent: 0 } };

                case "right":
                    return { leave: { xPercent: 100 }, enter: { xPercent: 0 } };

                default:
                    return { leave: { yPercent: 100 }, enter: { yPercent: 0 } };
            }
        }),
    };

    //* 函数列表
    const methods = {
        //* Modal关闭函数
        closeModal: () => {
            //* 隐藏弹出窗口
            refs.open.value = false;
            //* 响应关闭事件
            emit("close");
            //* Body显示被隐藏的滚动条
            document.body.style.overflow = "";
            if (document.body.scrollHeight > document.body.offsetHeight) {
                document.body.style.paddingRight = "";
            }
        },

        //* Modal启动函数
        openModal: () => {
            //* 回到顶部
            nodes.container.value?.scrollTo({ top: 0 });
            //* 显示弹出窗口
            refs.open.value = true;
            //* 响应Open事件
            emit("open");
            //* Body隐藏滚动条
            const size = document.body.offsetWidth - document.body.clientWidth;
            document.body.style.overflow = "hidden";
            if (document.body.scrollHeight > document.body.offsetHeight) {
                document.body.style.paddingRight = `${size}px`;
            }
        },

        //* Modal滚动条函数
        scrollTo: (options: ScrollToOptions) => {
            nodes.container.value?.scrollTo(options);
        },
    };

    //* 动态属性
    const binds = reactive({
        content: computed(() => {
            return { style: { padding: define.spacing } };
        }),

        container: computed(() => {
            //* 初始化返回数据
            const result: { [name: string]: any } = {
                width: "100vw",
                height: "100vh",
            };

            //* 根据弹窗方向进行属性设置
            if (define.direction == "right" || define.direction == "left") {
                //* 尺寸调整
                result.width = utility.isNumber(define.width) ? define.width + "px" : define.width;
                if (define.minWidth) result["min-width"] = utility.isNumber(define.minWidth) ? define.minWidth + "px" : define.minWidth;
                if (define.maxWidth) {
                    result["max-width"] = utility.isNumber(define.maxWidth) ? define.maxWidth + "px" : define.maxWidth;
                }

                //* 位置调整
                result.top = "0";
                if (define.direction == "left") result.left = "0";
                else {
                    result.right = "0";
                }
            } else {
                //* 尺寸调整
                result.height = utility.isNumber(define.height) ? define.height + "px" : define.height;
                if (define.minHeight) result["min-height"] = utility.isNumber(define.minHeight) ? define.minHeight + "px" : define.minHeight;
                if (define.maxHeight) {
                    result["max-height"] = utility.isNumber(define.maxHeight) ? define.maxHeight + "px" : define.maxHeight;
                }

                //* 位置调整
                result.left = "0";
                if (define.direction == "bottom") result.bottom = "0";
                else {
                    result.top = "0";
                }
            }

            return { style: result };
        }),
    });

    //* 响应事件
    const ons = {
        animation: {
            "before-enter": () => {
                nodes.container.value && gsap.set(nodes.container.value, Object.assign(computeds.site.value.leave, { opacity: 0 }));
            },
            "enter": (_el: Element, done: () => void) => {
                if (nodes.container.value) {
                    gsap.to(
                        nodes.container.value,
                        Object.assign(computeds.site.value.enter, { duration: 0.5, opacity: 1, onComplete: () => done() })
                    );
                }
            },
            "leave": (_el: Element, done: () => void) => {
                if (nodes.container.value) {
                    gsap.to(
                        nodes.container.value,
                        Object.assign(computeds.site.value.enter, { duration: 0.5, opacity: 0, onComplete: () => done() })
                    );
                }
            },
        },
    };

    return { ons, refs, nodes, binds, methods, computeds };
};
